import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function ReferralConfigs() {
  const styles = useThemeStyles();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    referralCode: '',
    referrerName: '',
    individualCommission: 20,
    teamCommission: 35,
    isActive: true
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/referral-configs');
      setConfigs(response.data);
    } catch (err) {
      setError('Failed to load referral configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setFormData({
      referralCode: '',
      referrerName: '',
      individualCommission: 20,
      teamCommission: 35,
      isActive: true
    });
    setOpenDialog(true);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      referralCode: config.referralCode,
      referrerName: config.referrerName,
      individualCommission: config.individualCommission,
      teamCommission: config.teamCommission,
      isActive: config.isActive
    });
    setOpenDialog(true);
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to deactivate this referral code?')) {
      return;
    }

    try {
      await api.delete(`/api/admin/referral-configs/${configId}`);
      setSuccess('Referral code deactivated successfully');
      loadConfigs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deactivate referral code');
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (editingConfig) {
        await api.put(`/api/admin/referral-configs/${editingConfig._id}`, formData);
        setSuccess('Referral configuration updated successfully');
      } else {
        await api.post('/api/admin/referral-configs', formData);
        setSuccess('Referral configuration created successfully');
      }
      setOpenDialog(false);
      loadConfigs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save referral configuration');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={styles.layout.flexBetween}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Referral Code Configurations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={styles.button?.primary}
        >
          Add Referral Code
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Referral Code</TableCell>
              <TableCell>Referrer Name</TableCell>
              <TableCell>Individual Commission</TableCell>
              <TableCell>Team Commission</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config) => (
              <TableRow key={config._id}>
                <TableCell>
                  <Chip label={config.referralCode} size="small" />
                </TableCell>
                <TableCell>{config.referrerName}</TableCell>
                <TableCell>£{config.individualCommission.toFixed(2)}</TableCell>
                <TableCell>£{config.teamCommission.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip
                    label={config.isActive ? 'Active' : 'Inactive'}
                    color={config.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(config)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(config._id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Referral Code' : 'Create Referral Code'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Referral Code"
              value={formData.referralCode}
              onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
              disabled={!!editingConfig}
              required
              helperText={editingConfig ? 'Referral code cannot be changed' : 'Uppercase letters, numbers, hyphens, and underscores only'}
            />
            <TextField
              label="Referrer Name"
              value={formData.referrerName}
              onChange={(e) => setFormData({ ...formData, referrerName: e.target.value })}
              required
            />
            <TextField
              label="Individual Commission (£)"
              type="number"
              value={formData.individualCommission}
              onChange={(e) => setFormData({ ...formData, individualCommission: parseFloat(e.target.value) || 0 })}
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Team Commission (£)"
              type="number"
              value={formData.teamCommission}
              onChange={(e) => setFormData({ ...formData, teamCommission: parseFloat(e.target.value) || 0 })}
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReferralConfigs;

